<script lang="ts">
  import { db } from '$lib/db/db';
  import { formatDateTime } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { downloadText } from '$lib/media/download';
  import { calendarEventIcs } from '$lib/media/ics';
  import { defang } from '$lib/protocol/link-guard';
  import { parseElement, type CalendarEventElement } from '$lib/protocol/types';

  import type { ElementContext } from './context';

  let { element, ctx }: { element: CalendarEventElement; ctx: ElementContext } = $props();

  // Two elements with the same event_id describe the same event; the occurrence
  // with the higher sequence replaces the older one WHOLESALE. This card renders
  // the current state in place, so an old bubble never shows a stale time.
  let override = $state<{ element: CalendarEventElement; sequence: number } | undefined>(undefined);

  $effect(() => {
    void db.calendarEntries.get(`${ctx.accountId}/${element.eventId}`).then((row) => {
      if (row === undefined || row.sourceSequence <= ctx.sequence) {
        return;
      }
      try {
        const parsed = parseElement(JSON.parse(row.elementJson));
        if (parsed.type === 'calendar_event') {
          override = { element: parsed, sequence: row.sourceSequence };
        }
      } catch {
        // A malformed stored row falls back to the element as sent.
      }
    });
  });

  const current = $derived(override?.element ?? element);
  const currentSequence = $derived(override?.sequence ?? ctx.sequence);

  const cancelled = $derived(current.status === 'cancelled');

  // The add-to-calendar action is a downloadable .ics file — the platform-neutral
  // equivalent of a system calendar insert; nothing is written unprompted. The
  // stable UID plus SEQUENCE makes a re-download an update, not a duplicate.
  function saveIcs(): void {
    downloadText(
      calendarEventIcs(current, { accountId: ctx.accountId, sequence: currentSequence }),
      'text/calendar',
      `event-${element.eventId}.ics`,
    );
  }
</script>

<div class="calendar" class:cancelled>
  <div class="head">
    <span aria-hidden="true">📅</span>
    <strong>{defang(current.title.length > 0 ? current.title : t('el.calendar.title'))}</strong>
    {#if cancelled}<span class="badge">{t('el.calendar.cancelled')}</span>{/if}
  </div>
  <p class="when">
    {formatDateTime(current.startsAt)}{current.endsAt !== undefined
      ? ` – ${formatDateTime(current.endsAt)}`
      : ''}
  </p>
  {#if current.place !== undefined}<p class="meta">{defang(current.place)}</p>{/if}
  {#if current.note !== undefined}<p class="meta">{defang(current.note)}</p>{/if}
  {#if !cancelled}
    <button onclick={saveIcs}>{t('el.calendar.download')}</button>
  {/if}
</div>

<style>
  .calendar {
    min-width: 220px;
  }
  .cancelled .when,
  .cancelled .head strong {
    text-decoration: line-through;
  }
  .head {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .badge {
    font-size: 12px;
    background: var(--warn-bg);
    color: var(--warn-ink);
    border-radius: 999px;
    padding: 2px 8px;
  }
  .when {
    margin: 6px 0 2px;
    font-weight: 600;
  }
  .meta {
    margin: 2px 0;
    font-size: 14px;
    color: var(--ink-soft);
  }
  button {
    margin-top: 8px;
    border: 1px solid var(--brand);
    background: transparent;
    color: var(--brand);
    border-radius: 999px;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
  }
</style>
